import type { InjectionKey, MaybeRefOrGetter } from 'vue';
import { computed, inject, provide, shallowReactive, toValue } from 'vue';

export type BlockEditorChildBoundsElement = HTMLElement | SVGElement;

export interface BlockEditorChildBoundsRegistry {
	getChildBoundsElement: (childId: string) => BlockEditorChildBoundsElement | null;
	setChildBoundsElement: (childId: string, element: BlockEditorChildBoundsElement | null) => void;
	pruneChildBoundsElements: (childIds: string[]) => void;
}

const blockEditorChildBoundsRegistryKey = Symbol('blockEditorChildBoundsRegistry') as InjectionKey<BlockEditorChildBoundsRegistry>;

export function useProvideBlockEditorChildBoundsRegistry(): BlockEditorChildBoundsRegistry {
	const childBoundsElements = shallowReactive<Record<string, BlockEditorChildBoundsElement>>({});

	function getChildBoundsElement(childId: string) {
		return childBoundsElements[childId] ?? null;
	}

	function setChildBoundsElement(childId: string, element: BlockEditorChildBoundsElement | null) {
		if (element) {
			childBoundsElements[childId] = element;
		}
		else {
			delete childBoundsElements[childId];
		}
	}

	function pruneChildBoundsElements(childIds: string[]) {
		const activeChildIds = new Set(childIds);

		for (const childId of Object.keys(childBoundsElements)) {
			if (!activeChildIds.has(childId)) {
				delete childBoundsElements[childId];
			}
		}
	}

	const registry = {
		getChildBoundsElement,
		setChildBoundsElement,
		pruneChildBoundsElements,
	};

	provide(blockEditorChildBoundsRegistryKey, registry);

	return registry;
}

export function useBlockEditorChildBoundsElement(childId: MaybeRefOrGetter<string>) {
	const registry = inject(blockEditorChildBoundsRegistryKey, null);

	return computed(() => {
		if (!registry) {
			return null;
		}

		return registry.getChildBoundsElement(toValue(childId));
	});
}
